import { createFileRoute } from '@tanstack/react-router';
import { PageShell } from '~/components/layout';
import {
  CountrySelectorCard,
  CtaBanner,
  FaqAccordion,
  FeatureBlock,
  Hero,
  InvestorsGrid,
  TestimonialGrid,
  TextSection,
} from '~/components/sections';
import type { FaqContent } from '~/content/types';
import { DEFAULT_LANGUAGE } from '~/lib/language';
import { getDefaultFaq } from '~/lib/payload';
import { pageHeadMeta } from '~/lib/seo';
import { useContent, useLanguage } from '~/lib/useLanguage';
import { useSiteAvailability } from '~/lib/useSiteAvailability';
import heroCss from '~/styles/hero.css?inline';

/**
 * Words that mark an FAQ entry as stablecoin-specific, so it can be hidden while
 * the feature is unavailable. Arabic terms are listed alongside the English ones
 * because the pattern runs against whichever language the page is rendering.
 */
const STABLECOIN_FAQ_PATTERN =
  /\b(?:stablecoin|cryptocurrency|crypto)\b|عملة مستقرة|العملات المستقرة|كريبتو/iu;

export const Route = createFileRoute('/')({
  loader: () => getDefaultFaq(),
  head: () =>
    pageHeadMeta({
      title: 'Open a Global USD Account and Send Money Today',
      description:
        'Open a global USD account with nsave. Get U.S. account and routing numbers to receive ACH payments and send money internationally with low fees.',
      path: '/',
      inlineStyles: [heroCss],
    }),
  component: AccountsPage,
});

function AccountsPage() {
  const faqContent = Route.useLoaderData();
  return <AccountsPageContent faqContent={faqContent} />;
}

export function AccountsPageContent({ faqContent }: { faqContent: FaqContent }) {
  return (
    <PageShell>
      <AccountsPageBody faqContent={faqContent} />
    </PageShell>
  );
}

function AccountsPageBody({ faqContent }: { faqContent: FaqContent }) {
  const { stablecoin: stablecoinAvailable } = useSiteAvailability();
  const language = useLanguage();
  const { accounts, shared } = useContent();
  const { accountsHero, accountsReceive, accountsTransfer, accountsConvert } = accounts;

  // Payload serves the FAQ in English only, so a translated page uses the FAQ
  // from its own content bundle instead of the loader's.
  const localizedFaq = language === DEFAULT_LANGUAGE ? faqContent : shared.defaultFaq;

  const transferContent = stablecoinAvailable
    ? accountsTransfer
    : {
        ...accountsTransfer,
        // Matched on the language-independent row id — filtering on `title` broke
        // as soon as the row was translated.
        rows: accountsTransfer.rows.filter((row) => row.id !== 'stablecoin-wallet'),
      };
  const visibleFaqContent = stablecoinAvailable
    ? localizedFaq
    : {
        ...localizedFaq,
        items: localizedFaq.items.filter(
          ({ question, answer }) => !STABLECOIN_FAQ_PATTERN.test(`${question} ${answer}`),
        ),
      };

  return (
    <>
      <Hero content={accountsHero} showBalanceCard />
      <TextSection content={shared.trustedBySection} />
      <FeatureBlock
        content={accountsReceive}
        imagePosition="left"
        sectionClassName="!py-[10px] max-lg:mb-16"
      />
      <FeatureBlock
        content={transferContent}
        imagePosition="left"
        sectionClassName="!py-[10px] max-lg:mb-16"
        imageOverlay={<CountrySelectorCard />}
      />
      <FeatureBlock
        content={accountsConvert}
        imagePosition="left"
        sectionClassName="!py-[10px] max-lg:mb-16"
      />
      <TextSection content={shared.safeSpaceSection} sectionClassName="max-md:!my-[40px]" />
      <InvestorsGrid content={shared.investorsSection} />
      <TestimonialGrid content={shared.defaultTestimonials} />
      <FaqAccordion content={visibleFaqContent} />
      <CtaBanner content={shared.defaultCtaBanner} />
    </>
  );
}
